import { createSlice } from "@reduxjs/toolkit";
import {
  fetchBookings,
  fetchBookingDetails,
  addNewBooking,
  removeBooking,
  updateBooking,
} from "./thunk";

const initialState = {
  bookings: [],
  bookingDetails: null,
  pagination: {
    page: 1,
    limit: 10,
    totalPages: 0,
    totalBookings: 0,
  },
  loading: false,
  error: "",
  success: "",
};

const BookingSlice = createSlice({
  name: "Bookings",
  initialState,
  reducers: {
    resetBookingFlag(state) {
      state.success = null;
      state.error = null;
    },
    clearBookingDetails(state) {
      state.bookingDetails = null;
    },
    setBookings(state, action) {
      state.bookings = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBookings.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(fetchBookings.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload && action.payload.data) {
          // Handle different response structures
          if (Array.isArray(action.payload.data)) {
            state.bookings = action.payload.data;
            state.pagination = {
              ...state.pagination,
              totalBookings: action.payload.data.length,
              totalPages: 1
            };
          } else if (action.payload.data.bookings) {
            state.bookings = action.payload.data.bookings;

            // ✅ FIX: Properly extract pagination data
            const paginationData = action.payload.data.pagination || action.payload.data;

            state.pagination = {
              page: paginationData.page || state.pagination.page,
              limit: paginationData.limit || state.pagination.limit,
              totalPages: paginationData.totalPages ||
                Math.ceil((paginationData.total || paginationData.totalBookings || 0) /
                  (paginationData.limit || state.pagination.limit)),
              totalBookings: paginationData.total || paginationData.totalBookings ||
                paginationData.totalCount || action.payload.data.bookings.length
            };

            // Also check if summary data exists
            if (action.payload.data.summary) {
              state.summary = action.payload.data.summary;
            }
          } else if (action.payload.data.rows) {
            // Handle findAndCountAll structure
            state.bookings = action.payload.data.rows;
            state.pagination = {
              page: action.payload.data.page || state.pagination.page,
              limit: action.payload.data.limit || state.pagination.limit,
              totalPages: action.payload.data.totalPages ||
                Math.ceil(action.payload.data.count /
                  (action.payload.data.limit || state.pagination.limit)),
              totalBookings: action.payload.data.count || action.payload.data.rows.length
            };
          }
        }
        state.error = "";
      })
      .addCase(fetchBookings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch bookings";
      })

      .addCase(fetchBookingDetails.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(fetchBookingDetails.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload && action.payload.data) {
          state.bookingDetails = action.payload.data;
        }
      })
      .addCase(fetchBookingDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch booking details";
      })

      .addCase(addNewBooking.pending, (state) => {
        state.loading = true;
        state.error = "";
        state.success = "";
      })
      .addCase(addNewBooking.fulfilled, (state, action) => {
        state.loading = false;
        state.success = "Booking created successfully";
        // Add new booking to list if available
        if (action.payload.data) {
          state.bookings.unshift(action.payload.data);
        }
      })
      .addCase(addNewBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create booking";
      })

      .addCase(updateBooking.pending, (state) => {
        state.loading = true;
        state.error = "";
        state.success = "";
      })
      .addCase(updateBooking.fulfilled, (state, action) => {
        state.loading = false;
        state.success = "Booking updated successfully";
        // Update booking in list if available
        if (action.payload.data) {
          const index = state.bookings.findIndex(b => b.id === action.payload.data.id);
          if (index !== -1) {
            state.bookings[index] = action.payload.data;
          }
        }
      })
      .addCase(updateBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update booking";
      })

      .addCase(removeBooking.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(removeBooking.fulfilled, (state, action) => {
        state.loading = false;
        state.bookings = state.bookings.filter((b) => b.id !== action.payload);
        state.success = "Booking deleted successfully";
      })
      .addCase(removeBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete booking";
      });
  },
});

export const { resetBookingFlag, clearBookingDetails, setBookings } = BookingSlice.actions;
export default BookingSlice.reducer;